import React from 'react';
import { API } from '../api';
import { Field, Section, Segmented, Toggle } from './ui';
import GeneratingOverlay, { newJobId } from './GeneratingOverlay';
import BriefImprover from './BriefImprover';
import NewsletterFormatBar from './NewsletterFormatBar';
import { safeHtml } from '../utils/safeHtml';

/*
 * The club newsletter.
 *
 * Four decisions kept visibly separate, because they are four different
 * decisions. What goes in it — ticked from what the club actually has, so
 * nobody is choosing sections blind. How it is written — a voice and a
 * length, remembered between issues because a club sends the same shape of
 * letter every month. What it says — the notes box, the club's own news,
 * which is the part no database knows. And who gets it, answered with real
 * counts before anybody presses send.
 *
 * Nothing here sends on its own, and the draft lives on the server rather
 * than in this component's state — a half-written newsletter should survive
 * a closed tab, and the person who drafts it is often not the person who
 * signs it off.
 */

const monthNow = () => new Date().toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });

const fmtSent = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-NZ', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

/* Where each ticked section's facts come from, so the box can show what is
   actually in it. A section the club has nothing for is shown but disabled:
   "Notable wins (nothing yet)" tells an admin something, where hiding the
   line entirely just leaves them wondering where it went. */
/* A line with a key can be ticked in or out on its own: a club with ten
   competitions open may only want to push two of them this month. A plain
   string is a description of the section, not something to choose. */
const SECTION_ITEMS = {
  events: g => g.events.map(e => ({ key: e.key, label: e.name + ' — ' + e.when })),
  openForEntry: g => g.openForEntry.map(c => ({ key: c.key, label: c.name + ' — ' + c.when })),
  results: g => g.finished.map(c => ({ key: c.key, label: c.name })),
  notableWins: g => g.notableWins.map(w => ({ key: w.key, label: w.winner + ' — ' + w.competition })),
  ladder: g => g.ladder.map(l => ({ key: l.key, label: l.position + '. ' + l.name })),
  nextMonth: g => g.nextMonth.map(e => ({ key: e.key, label: e.name + ' — ' + e.when })),
  posters: g => g.posters.map(p => p.title),
  membership: () => ['A join-or-renew link'],
  wordFromClub: () => ['Written from your brief below'],
};

/** One ticked section, with the things it would put in the letter. */
function SectionBox({ section, items, on, onChange, excluded, onToggleItem }) {
  const empty = !items.length;
  const pickable = items.length > 0 && typeof items[0] === 'object';
  const chosen = pickable ? items.filter(it => !excluded.has(it.key)).length : items.length;
  return (
    <div className={`newsletter-section-box${empty ? ' is-empty' : ''}`}>
      <label className="newsletter-tick">
        <input type="checkbox" className="w3-check" checked={on && !empty}
          disabled={empty} onChange={e => onChange(e.target.checked)} />
        <span className="newsletter-tick-label">{section.label}</span>
        {pickable && on && <span className="newsletter-tick-count">{chosen} of {items.length}</span>}
      </label>
      {empty ? (
        <p className="newsletter-tick-empty">Nothing for this yet</p>
      ) : pickable ? (
        /* Every item, each with its own box. Keyed by the item's key, which is
           its record's id, so two competitions with the same name and no date
           settled still show as two lines. */
        <ul className={`newsletter-sources newsletter-pick-list${on ? '' : ' is-off'}`}>
          {items.map(it => (
            <li key={it.key}>
              <label className="newsletter-pick">
                <input type="checkbox" className="gs-check" checked={!excluded.has(it.key)}
                  disabled={!on} onChange={e => onToggleItem(it.key, e.target.checked)} />
                <span>{it.label}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="newsletter-sources">
          {items.slice(0, 6).map((line, i) => <li key={i}>{line}</li>)}
          {items.length > 6 && <li className="newsletter-more">and {items.length - 6} more</li>}
        </ul>
      )}
      {section.hint && !empty && <p className="newsletter-tick-hint">{section.hint}</p>}
    </div>
  );
}

/**
 * The draft's HTML with its pictures made visible to the person reading the
 * proof.
 *
 * The letter points its images at public addresses that are deliberately
 * closed until it is actually sent — a poster or a photo nobody has published
 * should not be sitting on a public URL. That is right for the world and
 * wrong for this screen: the admin asked to see the letter before it goes,
 * and would have been shown broken images where the pictures will be.
 *
 * Posters are fetched through the authenticated route the admin can already
 * reach. The issue's own photos are already in hand, on the draft. The public
 * rule is untouched; only the proof changes.
 */
/* One pattern for both kinds of picture, and it is anchored on src="…" for a
   reason that cost an afternoon. The first version was two patterns, each
   beginning with an unbounded [^"']*, chained so the second ran over the
   output of the first — a 1.1MB base64 data url with no quote in it. At every
   one of a million positions the engine ran [^"']* to the end of the string
   and backtracked looking for /newsletter/: quadratic, about half an hour of
   solid CPU, and the browser tab died before it finished. Measured: 2.2
   seconds for 40KB.
   Bounding the wildcard inside the quotes means it can never run past the end
   of the attribute, and doing both substitutions in one pass means neither
   ever sees the substituted image data at all. */
const PICTURE_SRC = /src=(["'])[^"']*\/(poster|club-photo)\/(\d+)\.jpg\1/g;

/* Nothing, drawn. A picture's slot holds this until it arrives: leaving the
   public url in place means the browser fetches an address that is closed,
   gets a 404 and paints a broken image while it waits. */
const BLANK_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function usePreviewHtml(draft, gallery) {
  const bodyHtml = draft && draft.bodyHtml;
  const photos = gallery || [];
  const [html, setHtml] = React.useState('');

  /* Compared by id rather than by identity: the draft object is replaced on
     every keystroke in the subject box, and refetching every picture because
     somebody typed a letter would be absurd. */
  const photoKey = photos.map(p => p.id).join(',');

  React.useEffect(() => {
    let cancelled = false;
    /* Cleaned before anything else touches it: this is model output, shown in
       the admin's own session, and the editor starts from it too. See
       utils/safeHtml.js. */
    const source = safeHtml(bodyHtml || '');

    /* The gallery list carries thumbnails, which is all a proof needs and a
       fraction of the bytes. Anything the letter shows at full width is a
       poster, and those are fetched at full size below. */
    const byId = {};
    photos.forEach(p => { byId[p.id] = p.thumb; });
    /* Always over `source`, never over its own output. */
    /* Each swap remembers the address it replaced. The preview is editable,
       so this HTML can come back to be saved — and saving a megabyte of
       base64 into the letter instead of the six-character URL it stands for
       would bloat the row and break the images for everyone but the admin who
       edited it. restoreSrcs puts them all back before anything is stored. */
    const render = posters => source.replace(PICTURE_SRC, (whole, q, kind, id) => {
      const original = whole.slice(5).replace(/^["']|["']$/g, '');
      const isPoster = kind === 'poster';
      const data = isPoster ? posters[id] : byId[id];
      return 'src=' + q + (data || BLANK_PIXEL) + q + ' data-gs-src=' + q + original + q;
    });

    const ids = [...new Set([...source.matchAll(PICTURE_SRC)]
      .filter(m => m[2] === 'poster')
      .map(m => m[3]))];
    setHtml(render({}));
    if (!ids.length) return undefined;

    (async () => {
      const found = {};
      await Promise.all(ids.map(async id => {
        const r = await fetch(`${API}/api/posters/${id}`);
        if (!r.ok) return;
        const p = await r.json().catch(() => null);
        if (p && p.imageData) found[id] = p.imageData;
      }));
      // Anything that could not be fetched keeps its blank.
      if (!cancelled) setHtml(render(found));
    })();

    return () => { cancelled = true; };
  }, [bodyHtml, photoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return html;
}

/**
 * Turn edited HTML back into something worth storing.
 *
 * Done through the DOM rather than with a regular expression, deliberately.
 * This string contains the full base64 of every picture in the letter — over
 * a megabyte of quote-free text — and a wildcard pattern loose enough to find
 * an attribute in it is a wildcard pattern that backtracks catastrophically
 * over it. Walking the parsed nodes is linear and cannot misfire.
 */
function restoreSrcs(html) {
  const holder = document.createElement('div');
  holder.innerHTML = html;
  holder.querySelectorAll('img[data-gs-src]').forEach(img => {
    img.setAttribute('src', img.getAttribute('data-gs-src'));
    img.removeAttribute('data-gs-src');
  });
  return holder.innerHTML;
}


/* The markup one photo becomes when it is dropped into the letter. Sized and
   styled inline because this ends up in an email, where a stylesheet would be
   thrown away. */
function photoFigure({ url, data, caption }) {
  const fig = document.createElement('figure');
  fig.setAttribute('style', 'margin:16px 0');
  const img = document.createElement('img');
  img.setAttribute('src', data);
  img.setAttribute('data-gs-src', url);
  img.setAttribute('alt', caption || '');
  img.setAttribute('width', '560');
  img.setAttribute('style', 'max-width:100%;height:auto;display:block;border-radius:6px');
  fig.appendChild(img);
  if (caption) {
    const cap = document.createElement('figcaption');
    cap.setAttribute('style', 'font-size:13px;color:#666;font-style:italic;margin-top:6px');
    cap.textContent = caption;
    fig.appendChild(cap);
  }
  return fig;
}

/* --------------------------------- who gets it ----------------------- */

const FINANCIAL_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'financial', label: 'Financial' },
  { value: 'unfinancial', label: 'Unfinancial' },
  { value: 'due-soon', label: 'Renewal due' },
];

function TypeFilter({ label, help, values, chosen, onChange }) {
  if (!values.length) return null;
  const toggle = v => onChange(chosen.includes(v)
    ? chosen.filter(x => x !== v)
    : [...chosen, v]);
  return (
    <Field label={label} help={help}>
      <div className="newsletter-chips">
        {values.map(v => (
          <label key={v.value}
            className={`newsletter-chip${chosen.includes(v.value) ? ' is-on' : ''}`}>
            <input type="checkbox" checked={chosen.includes(v.value)}
              onChange={() => toggle(v.value)} />
            {v.value}
            <span className="newsletter-chip-count">{v.reachable}</span>
          </label>
        ))}
      </div>
    </Field>
  );
}

function NewsletterTab({ clubId, showToast, showConfirm }) {
  const [options, setOptions] = React.useState(null);
  const [sources, setSources] = React.useState(null);
  const [gallery, setGallery] = React.useState([]);
  const [settings, setSettings] = React.useState(null);
  const [list, setList] = React.useState([]);
  const [draft, setDraft] = React.useState(null);
  const [period, setPeriod] = React.useState(monthNow());
  const [context, setContext] = React.useState('');
  const [posterIds, setPosterIds] = React.useState([]);
  // Diary items left out of this issue, by key ('comp-12'); everything else is in.
  const [excludeItems, setExcludeItems] = React.useState([]);
  // The sent issue whose letter is open for reading in the history.
  const [viewingSent, setViewingSent] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  /* The job the server is reporting on, and whether its overlay is still
     showing. Kept apart: dismissing the overlay must not abandon the job. */
  const [job, setJob] = React.useState(null);
  const [watching, setWatching] = React.useState(false);
  const [error, setError] = React.useState('');
  /* 'read' shows it, 'write' lets an admin change the letter itself — the
     formatted thing, not its markup — and 'html' is the escape hatch for
     anyone who actually wants the tags. Most club secretaries will never
     touch the third. */
  const [mode, setMode] = React.useState('read');
  /* The HTML the editor was opened with. Held apart from previewHtml so that
     a poster finishing its download mid-sentence cannot overwrite what
     somebody is typing. */
  const [seed, setSeed] = React.useState('');
  /* Whether the letter has been touched is a ref and not state, and that is
     load-bearing rather than a micro-optimisation. This div is the browser's
     editing surface: React seeds its innerHTML once and must never write to
     it again. Setting state on every keystroke re-rendered the component, the
     re-render put the seeded HTML back, and the typing vanished — measured,
     with a photo dropped in disappearing the same way. Nothing that happens
     while somebody is typing may cause a render. */
  const dirtyRef = React.useRef(false);
  const editorRef = React.useRef(null);

  // Who gets it.
  const [audience, setAudience] = React.useState(null);
  const [membershipTypes, setMembershipTypes] = React.useState([]);
  const [playerTypes, setPlayerTypes] = React.useState([]);
  const [financial, setFinancial] = React.useState('any');
  // '' is everyone; otherwise only people interested in this mailing-list topic.
  const [topic, setTopic] = React.useState('');
  const [userIds, setUserIds] = React.useState([]);
  const [pickNames, setPickNames] = React.useState(false);
  const [reach, setReach] = React.useState(null);

  const bodyRef = React.useRef(null);
  const qs = clubId ? '?clubId=' + clubId : '';

  const load = React.useCallback(async () => {
    setError('');
    const [o, s, st, l, r, g] = await Promise.all([
      fetch(`${API}/api/newsletters/options`),
      fetch(`${API}/api/newsletters/sources${qs}`),
      fetch(`${API}/api/newsletters/settings${qs}`),
      fetch(`${API}/api/newsletters${qs}`),
      fetch(`${API}/api/newsletters/recipients${qs}`),
      fetch(`${API}/api/gallery${qs}`),
    ]);
    if (g.ok) setGallery((await g.json()).photos);
    if (o.ok) setOptions(await o.json());
    if (st.ok) setSettings(await st.json());
    if (r.ok) setAudience(await r.json());
    if (s.ok) setSources(await s.json());
    else setError((await s.json().catch(() => ({}))).error || 'Could not see what is on.');
    if (l.ok) {
      const rows = await l.json();
      setList(rows);
      /* Pick up an unsent draft rather than starting a second one. The
         person opening this tab is usually coming back to something. */
      const open = rows.find(n => n.status === 'draft');
      if (open) {
        setDraft(open);
        setPeriod(open.period || monthNow());
        setContext(open.context || '');
        setPosterIds((open.posterIds || []).map(String));
        setExcludeItems((open.excludeItems || []).map(String));
      }
    }
  }, [qs]);

  React.useEffect(() => { load(); }, [load]);

  /* How many people the current filter actually reaches, kept live. An admin
     should never have to press send to find out it was going to nobody. */
  React.useEffect(() => {
    if (!draft) { setReach(null); return undefined; }
    let cancelled = false;
    (async () => {
      const r = await fetch(`${API}/api/newsletters/${draft.id}/preview-recipients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ membershipTypes, playerTypes, financial, userIds, topic: topic || undefined }),
      });
      if (!r.ok || cancelled) return;
      setReach(await r.json());
    })();
    return () => { cancelled = true; };
  }, [draft, membershipTypes, playerTypes, financial, userIds, topic]);

  const patchSettings = next => setSettings(s => ({ ...s, ...next }));
  const setInclude = (id, on) => setSettings(s => ({ ...s, include: { ...s.include, [id]: on } }));

  /* An item's tick belongs to this issue, not to the club's standing
     settings, so it is kept on the draft — saved straight away when there is
     one, so it survives the tab being closed before the letter is written. */
  const toggleItem = (key, on) => {
    const next = on ? excludeItems.filter(k => k !== key) : [...excludeItems, key];
    setExcludeItems(next);
    if (draft) {
      fetch(`${API}/api/newsletters/${draft.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excludeItems: next }),
      }).catch(() => {});
    }
  };

  /* A sent issue is a record and stays as it went. Editing and sending it
     again works on a copy, opened here as the draft. */
  const copySent = async (n) => {
    setError('');
    setBusy('copy');
    try {
      const r = await fetch(`${API}/api/newsletters/${n.id}/copy`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { showToast(data.error || 'It could not be copied.', 'red'); return; }
      setViewingSent(null);
      dirtyRef.current = false;
      setMode('read');
      await load();
      showToast('Copied as a new draft — change it, or send it as it is', 'green');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally { setBusy(''); }
  };

  const rememberDefaults = async () => {
    const r = await fetch(`${API}/api/newsletters/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clubId, ...settings }),
    });
    if (!r.ok) return showToast('Those could not be saved', 'red');
    showToast('Saved — next month opens like this', 'green');
    return setSettings(await r.json());
  };

  /* A brief taken from the suggestion is the admin's brief from then on,
     saved to the draft like anything they typed. */
  const applyBrief = text => {
    setContext(text);
    if (draft) saveEdits({ context: text }).catch(() => {});
  };

  const generate = async () => {
    /* An unanswered [TBC] is left out of the letter rather than guessed, so
       the admin should know it is still there before a minute is spent. */
    if (/\[TBC/i.test(context)) {
      const ok = await showConfirm('Your brief still has questions marked [TBC].'
        + '\n\nThose details will be left out of the newsletter. Write it anyway?');
      if (!ok) return;
    }
    const id = newJobId();
    setJob(id); setWatching(true);
    setBusy('generate'); setError('');
    try {
      const r = await fetch(`${API}/api/newsletters/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clubId, period, context, posterIds, excludeItems, jobId: id,
          id: draft ? draft.id : undefined,
          ...settings,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setError(data.error || 'It could not be written just now.'); return; }
      setDraft(data);
      setMode('read'); dirtyRef.current = false;
      showToast('Draft written — have a read before it goes', 'green');
    } finally { setBusy(''); setWatching(false); setJob(null); }
  };

  const saveEdits = async patch => {
    setDraft(d => ({ ...d, ...patch }));
    const r = await fetch(`${API}/api/newsletters/${draft.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!r.ok) showToast('That edit did not save', 'red');
  };

  /* An issue to hang photos on, made if there is not one yet. Photos belong
     to an issue and their public address contains its id, so before this an
     admin had to generate a letter they did not want in order to add the
     photographs that letter was supposed to be about. */
  const ensureDraft = async () => {
    if (draft) return draft;
    const r = await fetch(`${API}/api/newsletters/draft`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clubId, period }),
    });
    if (!r.ok) { showToast('Could not start an issue', 'red'); return null; }
    const made = await r.json();
    setDraft(made);
    return made;
  };

  /* Which gallery pictures this issue uses. The issue refers to the club's
     library rather than holding copies of its own: uploading happens once, in
     the Gallery, and two issues showing the same photograph show the same row
     rather than a second and third copy of it, each counted separately. */
  const togglePhoto = async photo => {
    const issue = await ensureDraft();
    if (!issue) return undefined;
    const current = (issue.photoIds || []).map(String);
    const next = current.includes(String(photo.id))
      ? current.filter(x => x !== String(photo.id))
      : [...current, String(photo.id)];
    const r = await fetch(`${API}/api/newsletters/${issue.id}/photos`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoIds: next }),
    });
    if (!r.ok) return showToast('That did not save', 'red');
    setDraft(await r.json());
    return undefined;
  };

  /* ---- editing the letter itself ---- */

  const openEditor = () => { setSeed(previewHtml); dirtyRef.current = false; setMode('write'); };

  /* The letter is put into the editor by hand, once, and React is given no
     way to put it back.
     dangerouslySetInnerHTML was the obvious way to seed it and it was wrong:
     React keeps owning that subtree, so any later render reconciles it back
     to the seeded HTML. Typing survived (React skips an unchanged string) but
     a photo inserted through the DOM did not — traced, it went in and came
     straight back out, four images to three, on the next render. With no
     __html prop there is nothing for React to reconcile against, and the
     browser keeps what the person actually did. */
  React.useEffect(() => {
    if (mode !== 'write' || !editorRef.current) return;
    editorRef.current.innerHTML = seed;
  }, [mode, seed]);

  const saveLetter = async () => {
    const el = editorRef.current;
    if (!el) return;
    const html = restoreSrcs(el.innerHTML);
    dirtyRef.current = false;
    await saveEdits({ bodyHtml: html });
  };

  const leaveEditor = async next => {
    if (dirtyRef.current) await saveLetter();
    setMode(next);
  };

  /* Entering the editor takes a copy of what is on screen; leaving it saves
     what came back. Everything else is just switching a view. */
  const changeMode = next => {
    if (next === mode) return;
    if (mode === 'write') { leaveEditor(next); return; }
    if (next === 'write') { openEditor(); return; }
    setMode(next);
  };

  /* Where the caret is, or the end of the letter if the caret is somewhere
     else entirely — an admin who clicks a photo without clicking into the
     text first means "put it at the bottom", not "do nothing". */
  const rangeInEditor = () => {
    const el = editorRef.current;
    if (!el) return null;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) return sel.getRangeAt(0);
    const end = document.createRange();
    end.selectNodeContents(el);
    end.collapse(false);
    return end;
  };

  const insertNode = (node, range) => {
    const el = editorRef.current;
    const at = range || rangeInEditor();
    if (!el || !at) return;
    at.deleteContents();
    at.insertNode(node);
    const after = document.createRange();
    after.setStartAfter(node);
    after.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(after);
    el.focus();
    dirtyRef.current = true;
  };

  /* The public base as the server sees it, not as this browser does. API is
     an empty string here — the client is served from the same origin — so
     building the url from it produced "/newsletter/11/photo/x.jpg", which is
     a broken image in every inbox: an email has no page to be relative to. */
  const placePhoto = (photo, range) => insertNode(photoFigure({
    url: `${sources.baseUrl || window.location.origin}/club-photo/${photo.id}.jpg`,
    data: photo.thumb,
    caption: photo.label,
  }), range);

  const placePoster = (poster, range) => insertNode(photoFigure({
    url: poster.url, data: poster.url, caption: '',
  }), range);

  /* Dropping a photo onto the letter puts it exactly where it was dropped.
     The two browsers name this differently and neither is standard, so both
     spellings are tried before falling back to the caret. */
  const rangeAtPoint = (x, y) => {
    if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(x, y);
      if (!pos) return null;
      const r = document.createRange();
      r.setStart(pos.offsetNode, pos.offset);
      r.collapse(true);
      return r;
    }
    return null;
  };

  const onDropPhoto = e => {
    const photoId = e.dataTransfer.getData('text/gs-photo');
    const posterId = e.dataTransfer.getData('text/gs-poster');
    if (!photoId && !posterId) return;
    e.preventDefault();
    const range = rangeAtPoint(e.clientX, e.clientY);
    if (photoId) {
      const photo = gallery.find(p => String(p.id) === photoId);
      if (photo) placePhoto(photo, range);
    } else {
      const poster = sources.posters.find(p => String(p.id) === posterId);
      if (poster) placePoster(poster, range);
    }
  };

  const discard = async () => {
    const ok = await showConfirm(
      'Throw this draft away?\n\nIt has not been sent to anybody.'
    );
    if (!ok) return;
    const r = await fetch(`${API}/api/newsletters/${draft.id}`, { method: 'DELETE' });
    if (!r.ok) return showToast('It could not be deleted', 'red');
    setDraft(null); setContext(''); setExcludeItems([]); setMode('read'); dirtyRef.current = false;
    return load();
  };

  const send = async ({ testOnly }) => {
    if (testOnly && !userIds.length) {
      return showToast('Tick who the test copy should go to', 'orange');
    }
    const count = reach ? reach.count : 0;
    const ok = await showConfirm(testOnly
      ? `Send a test copy to ${count} ${count === 1 ? 'person' : 'people'}?`
        + '\n\nThe issue stays a draft and can still be edited.'
      : `Send this newsletter to ${count} ${count === 1 ? 'person' : 'people'}?`
        + '\n\nIt cannot be edited or unsent afterwards.');
    if (!ok) return undefined;
    setBusy(testOnly ? 'test' : 'send'); setError('');
    try {
      const r = await fetch(`${API}/api/newsletters/${draft.id}/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ membershipTypes, playerTypes, financial, userIds, topic: topic || undefined, testOnly }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setError(data.error || 'It could not be sent.'); return undefined; }
      if (testOnly) {
        setDraft(data.newsletter);
        return showToast(`Test copy sent to ${data.sentTo}`, 'green');
      }
      showToast(`Sent to ${data.sentTo} ${data.sentTo === 1 ? 'member' : 'members'}`, 'green');
      setDraft(null); setContext(''); setExcludeItems([]);
      return load();
    } finally { setBusy(''); }
  };

  const previewHtml = usePreviewHtml(draft, gallery);
  const sent = list.filter(n => n.status === 'sent');
  const excludedSet = new Set(excludeItems);
  const chosenIds = ((draft && draft.photoIds) || []).map(String);

  if (!sources || !options || !settings) {
    return (
      <div className="newsletter-tab">
        {error
          ? <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>
          : <p className="w3-text-grey">Looking at what is on…</p>}
      </div>
    );
  }


  return (
    <div className="newsletter-tab">
      {busy === 'generate' && watching && job && (
        <GeneratingOverlay
          jobId={job}
          endpoint="/api/newsletters/progress"
          title="Writing your newsletter"
          typical="usually about a minute"
          fallbackSteps={['Reading the club diary', 'Writing the newsletter', 'Saving the draft']}
          onDismiss={() => setWatching(false)}
        />
      )}
      {/* Locked, not merely dimmed — see PosterStudio. The overlay stops the
          mouse; inert also stops a keyboard tabbing into the notes behind it
          and editing a brief that has already gone. */}
      <div inert={busy === 'generate' || undefined}>
      {error && <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>}

      <Section label="What goes in it">
        <div className="newsletter-section-grid">
          {options.sections.map(s => (
            <SectionBox key={s.id} section={s}
              items={(SECTION_ITEMS[s.id] || (() => []))(sources)}
              on={settings.include[s.id] !== false}
              onChange={on => setInclude(s.id, on)}
              excluded={excludedSet} onToggleItem={toggleItem} />
          ))}
        </div>
      </Section>

      <Section label="How it reads"
        action={
          <button className="w3-button w3-small w3-border" onClick={rememberDefaults}>
            <i className="fa fa-thumb-tack w3-margin-right"></i>Remember for next month
          </button>
        }>
        <div className="gs-field-grid" style={{ '--gs-cols': '2fr 1fr' }}>
          <Field label="Voice" help={(options.tones.find(t => t.id === settings.tone) || {}).hint}>
            <Segmented ariaLabel="Voice" name="nl-tone" value={settings.tone}
              onChange={v => patchSettings({ tone: v })}
              options={options.tones.map(t => ({ value: t.id, label: t.label }))} />
          </Field>
          <Field label="Length">
            <Segmented ariaLabel="Length" name="nl-length" value={settings.length}
              onChange={v => patchSettings({ length: v })}
              options={options.lengths.map(l => ({ value: l.id, label: l.label }))} />
          </Field>
        </div>
        <Toggle label="Open with a club logo header band"
          checked={settings.logoHeader}
          onChange={v => patchSettings({ logoHeader: v })}
          style={{ marginTop: 'var(--gs-space-4)' }} />
      </Section>

      {sources.posters.length > 0 && settings.include.posters !== false && (
        <Section label="Posters to put in">
          <p className="w3-text-grey newsletter-note">
            A poster belonging to an event or competition already goes in with it. Tick any
            others you want included.
          </p>
          <div className="newsletter-poster-picker">
            {sources.posters.map(p => (
              <label key={p.id}
                className={`newsletter-poster-pick${posterIds.includes(String(p.id)) ? ' is-on' : ''}`}>
                <input type="checkbox" checked={posterIds.includes(String(p.id))}
                  onChange={e => setPosterIds(ids => (e.target.checked
                    ? [...ids, String(p.id)]
                    : ids.filter(x => x !== String(p.id))))} />
                <span>{p.title}</span>
                {p.attachedTo && <em>already with its {p.attachedTo}</em>}
              </label>
            ))}
          </div>
        </Section>
      )}

      <Section label={draft ? 'The draft' : 'Write one'}>
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 3fr' }}>
          <Field label="Issue" htmlFor="nl-period" help="Shown to nobody but you.">
            <input id="nl-period" className="w3-input" value={period}
              onChange={e => setPeriod(e.target.value)} placeholder={monthNow()} />
          </Field>
          <Field label="Your brief for this issue" htmlFor="nl-context"
            help="Tell the writer what this issue should be: what to lead with, what to push, what to leave out, and the club's own news. It follows these over the settings above. Facts must be in here or in the club's diary — it will not make any up.">
            <textarea id="nl-context" className="w3-input newsletter-context" rows={6}
              value={context} onChange={e => setContext(e.target.value)}
              placeholder={'Lead with opening day on Saturday 20 September — we want a big turnout, bring a friend.\nPush the Champ Pairs, entries close Friday.\nWorking bee on the 14th from 9am, all hands welcome. Thanks to Marg for the new flags.\nKeep it short and leave out the ladder this month.'} />
            <BriefImprover context={context} showToast={showToast}
              request={() => ({ clubId, context, posterIds, excludeItems, ...settings })}
              onUse={applyBrief} />
          </Field>
        </div>

        <div className="newsletter-actions">
          <button className="w3-button gs-btn-primary" onClick={generate}
            disabled={busy === 'generate'}>
            {busy === 'generate'
              ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Writing…</>
              : <><i className="fa fa-magic w3-margin-right"></i>{draft ? 'Write it again' : 'Write the newsletter'}</>}
          </button>
          {draft && (
            <button className="w3-button w3-border" onClick={discard} disabled={!!busy}>
              <i className="fa fa-trash w3-margin-right"></i>Throw it away
            </button>
          )}
        </div>
        {busy === 'generate' && (
          <p className="w3-text-grey newsletter-note">
            This takes half a minute. Writing it again replaces this draft rather than making
            another, so change anything above and press it as often as you like.
          </p>
        )}
      </Section>

      {/* No longer gated on a draft existing. Adding the first photo opens
          the issue, so the photographs can go in before the letter is written
          and be there when it is. */}
      <Section label="Photos in this issue"
        action={<span className="w3-text-grey newsletter-note">
          {gallery.length} in the club gallery
        </span>}>
        <p className="w3-text-grey newsletter-note">
          Tick the pictures this issue should use. They come from the club's gallery —
          upload new ones under the Gallery tab, and the same photograph can appear in
          any number of issues without being stored twice.
          {draft && draft.bodyHtml
            ? ' Write it again to have them placed for you, or put them exactly where you'
              + ' want under Edit.'
            : ' They will be worked into the letter when it is written.'}
        </p>
        {gallery.length === 0 ? (
          <p className="w3-text-grey">
            The gallery is empty. Add a photo under the Gallery tab and it will show up here.
          </p>
        ) : (
          <div className="newsletter-pick-grid">
            {gallery.map(p => {
              const on = chosenIds.includes(String(p.id));
              return (
                <label key={p.id} className={`newsletter-pick-photo${on ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => togglePhoto(p)} />
                  <img src={p.thumb} alt="" loading="lazy" />
                  <span>{p.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </Section>

      {draft && (
        <Section
          label="Read it before it goes"
          action={
            <Segmented
              ariaLabel="How to view the letter"
              name="nl-mode"
              value={mode}
              onChange={changeMode}
              options={[
                { value: 'read', label: 'Read' },
                { value: 'write', label: 'Edit' },
                { value: 'html', label: 'HTML' },
              ]}
            />
          }
        >
          <Field label="Subject" htmlFor="nl-subject">
            <input id="nl-subject" className="w3-input" value={draft.subject || ''}
              onChange={e => setDraft({ ...draft, subject: e.target.value })}
              onBlur={e => saveEdits({ subject: e.target.value })} />
          </Field>

          {mode === 'html' ? (
            /* The escape hatch. Almost nobody needs it, and the people who do
               would rather have it than a rich editor guessing at their
               markup. */
            <Field label="The letter, as HTML" htmlFor="nl-body"
              help="Styling has to be inline — mail clients throw stylesheets away.">
              <textarea id="nl-body" ref={bodyRef} className="w3-input newsletter-source" rows={20}
                value={draft.bodyHtml || ''}
                onChange={e => setDraft({ ...draft, bodyHtml: e.target.value })}
                onBlur={e => saveEdits({ bodyHtml: e.target.value })} />
            </Field>
          ) : mode === 'write' ? (
            <>
              <p className="w3-text-grey newsletter-note">
                Click anywhere and type. Select some words and use the bar to change their
                size, colour or style. To add a picture, put the cursor where you want it
                and click one below — or drag it straight onto the letter.
              </p>
              <NewsletterFormatBar editorRef={editorRef}
                colours={(sources.club && sources.club.colours) || {}}
                onChange={() => { dirtyRef.current = true; }} />
              {/* Uncontrolled on purpose. React must not own this subtree: it
                  is the browser's editing surface, and re-rendering it from
                  state on every keystroke would move the caret to the start of
                  the letter. It is seeded once when the editor opens and read
                  back when it closes. */}
              <div
                ref={editorRef}
                className="newsletter-preview newsletter-editable"
                contentEditable
                suppressContentEditableWarning
                onInput={() => { dirtyRef.current = true; }}
                onBlur={() => dirtyRef.current && saveLetter()}
                onDragOver={e => e.preventDefault()}
                onDrop={onDropPhoto}
              />
              {(gallery.length > 0 || sources.posters.length > 0) && (
                <div className="newsletter-tray">
                  <span className="newsletter-tray-label">Drag in, or click to place:</span>
                  {gallery.map(p => (
                    <button key={p.id} type="button" className="newsletter-tray-item"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/gs-photo', String(p.id))}
                      onClick={() => placePhoto(p)}
                      title={p.label}>
                      <img src={p.thumb} alt="" />
                      <span>{p.label.slice(0, 18)}</span>
                    </button>
                  ))}
                  {sources.posters.map(p => (
                    <button key={'poster' + p.id} type="button" className="newsletter-tray-item"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/gs-poster', String(p.id))}
                      onClick={() => placePoster(p)}
                      title={p.title}>
                      <i className="fa fa-file-image-o"></i>
                      <span>{p.title.slice(0, 18)}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="newsletter-actions">
                <button className="w3-button w3-border" onClick={() => leaveEditor('read')}>
                  <i className="fa fa-check w3-margin-right"></i>Save and read it
                </button>
                <span className="w3-text-grey">Changes are kept when you leave this view.</span>
              </div>
            </>
          ) : (
            /* The draft is written by a model from a prompt this server
               controls, edited only by the admin reading it, and shown only
               to that admin. It is the same HTML the members will get, so
               previewing anything less than the real thing would be a
               preview of something else. */
            <div className="newsletter-preview"
              dangerouslySetInnerHTML={{ __html: safeHtml(previewHtml) }} />
          )}
        </Section>
      )}

      {draft && audience && (
        <Section label="Who gets it">
          {(audience.topics || []).length > 0 && (
            <Field label="What it's about"
              help={topic
                ? "Only people who said they're interested in this. Members who haven't chosen get everything."
                : 'Goes to everyone on the list, whatever they ticked.'}
              style={{ marginBottom: 16 }}>
              <select className="w3-select" aria-label="What it's about" value={topic} onChange={e => setTopic(e.target.value)}
                style={{ maxWidth: 360 }}>
                <option value="">Everyone — general club news</option>
                {audience.topics.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
          )}
          <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            <TypeFilter label="Membership type" values={audience.membershipTypes}
              help={audience.untyped.membershipType
                ? `${audience.untyped.membershipType} members have no type set and are only reached by leaving this blank.`
                : 'Leave blank for all.'}
              chosen={membershipTypes} onChange={setMembershipTypes} />
            <TypeFilter label="Player type" values={audience.playerTypes}
              help={audience.untyped.playerType
                ? `${audience.untyped.playerType} members have no type set and are only reached by leaving this blank.`
                : 'Leave blank for all.'}
              chosen={playerTypes} onChange={setPlayerTypes} />
            <Field label="Subscription" help="Renewal due covers the next two months.">
              <Segmented ariaLabel="Subscription" name="nl-financial" value={financial}
                onChange={setFinancial} options={FINANCIAL_OPTIONS} />
            </Field>
          </div>

          <div className="newsletter-pick">
            <Toggle label="Choose people by name instead" checked={pickNames}
              onChange={v => { setPickNames(v); if (!v) setUserIds([]); }} />
            {pickNames && (
              <>
                <p className="w3-text-grey newsletter-note">
                  Names chosen here replace the filters above entirely. This is also how you
                  send yourself a test copy first.
                </p>
                <div className="newsletter-people">
                  {audience.members.map(m => (
                    <label key={m.id}
                      className={`newsletter-person${m.hasEmail ? '' : ' is-unreachable'}`}>
                      <input type="checkbox" disabled={!m.hasEmail}
                        checked={userIds.includes(m.id)}
                        onChange={e => setUserIds(ids => (e.target.checked
                          ? [...ids, m.id] : ids.filter(x => x !== m.id)))} />
                      <span>{m.name}</span>
                      {!m.hasEmail && <em>no email</em>}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="newsletter-send">
            <div className="newsletter-reach">
              {reach === null ? 'Counting…' : (
                <>
                  <strong>{reach.count}</strong>
                  {reach.count === 1 ? ' person' : ' people'} will get this
                  <span className="w3-text-grey">
                    {reach.subscribers ? ` (including ${reach.subscribers} on the mailing list who aren't members)` : ''}
                    {' '}— the club has {audience.total} members, of whom {audience.reachable} have an
                    email address on file. Anyone who has unsubscribed is left out.
                  </span>
                </>
              )}
            </div>
            <div className="newsletter-actions">
              <button className="w3-button w3-border" onClick={() => send({ testOnly: true })}
                disabled={!!busy || !userIds.length}>
                {busy === 'test'
                  ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Sending…</>
                  : <><i className="fa fa-flask w3-margin-right"></i>Send a test copy</>}
              </button>
              <button className="w3-button gs-btn-primary" onClick={() => send({ testOnly: false })}
                disabled={!!busy || !draft.subject || !draft.bodyHtml || !reach || !reach.count}>
                {busy === 'send'
                  ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Sending…</>
                  : <><i className="fa fa-paper-plane w3-margin-right"></i>Send it</>}
              </button>
            </div>
          </div>
        </Section>
      )}

      {sent.length > 0 && (
        <Section label="Already sent">
          {/* A sent letter is a record of what went out, so it is never changed
              or deleted. Editing and sending again works on a copy, which
              becomes the draft above; the original stays as it went. */}
          <ul className="newsletter-history">
            {sent.map(n => (
              <li key={n.id}>
                <div className="newsletter-history-row">
                  <span>
                    <strong>{n.subject}</strong>
                    <span className="w3-text-grey">
                      {' '}— {n.period}, sent {fmtSent(n.sentAt)} to {n.sentCount}{' '}
                      {n.sentCount === 1 ? 'member' : 'members'}
                    </span>
                  </span>
                  <span className="newsletter-history-actions">
                    <button className="w3-button w3-small w3-border"
                      aria-expanded={viewingSent === n.id}
                      onClick={() => setViewingSent(v => (v === n.id ? null : n.id))}>
                      <i className={`fa ${viewingSent === n.id ? 'fa-eye-slash' : 'fa-eye'} w3-margin-right`}></i>
                      {viewingSent === n.id ? 'Hide' : 'View'}
                    </button>
                    <button className="w3-button w3-small w3-border" onClick={() => copySent(n)}
                      disabled={!!busy}
                      title="Opens a copy as a new draft, to change and send again. The original stays as it went.">
                      <i className="fa fa-copy w3-margin-right"></i>Edit and resend
                    </button>
                  </span>
                </div>
                {viewingSent === n.id && (
                  <div className="newsletter-preview newsletter-history-proof"
                    dangerouslySetInnerHTML={{ __html: safeHtml(n.bodyHtml || '') }} />
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      </div>
    </div>
  );
}

export default NewsletterTab;
